import { LogEntry } from '@monorepo/shared/componentsV2/logs/types';
import { FieldResponse } from 'mapistry-shared';
import type { LogEntriesFormValues } from '../conversionHelpers';

export enum EntryStatus {
  INCOMPLETE = 'incomplete',
  COMPLETE_REVIEWED = 'complete-reviewed',
  COMPLETE_NOT_REVIEWED = 'complete-not-reviewed',
}

export function isLogEntryComplete({
  entryId,
  logEntry,
  errors,
  formValues,
  logFields,
}: {
  entryId: string;
  logEntry: LogEntry | undefined;
  errors: Record<string, unknown> | undefined;
  formValues: Partial<LogEntriesFormValues> | undefined;
  logFields: FieldResponse[] | undefined;
}): boolean {
  const entryErrors = errors?.[entryId];
  if (entryErrors && Object.keys(entryErrors).length > 0) return false;

  const requiredFields = logFields?.filter((field) => field.isRequired) ?? [];
  const entryFormValues = formValues?.[entryId];

  // Two paths depending on whether the entry has been rendered/visited/touched yet.
  // 1. Existing form values
  if (entryFormValues) {
    return (
      !!entryFormValues.logDate &&
      requiredFields.every((field) => {
        const value = entryFormValues[field.id];
        return value != null && value !== '';
      })
    );
  }

  // 2. No form values yet
  return (
    !!logEntry?.logDate &&
    requiredFields.every((field) => {
      const value = logEntry.logFieldValues?.[field.id]?.fieldValue?.value;
      return value != null && value !== '';
    })
  );
}

export function getLogEntryStatus({
  logEntry,
  errors,
  formValues,
  logFields,
}: {
  logEntry: LogEntry;
  errors: Record<string, unknown> | undefined;
  formValues: Partial<LogEntriesFormValues> | undefined;
  logFields: FieldResponse[] | undefined;
}): EntryStatus {
  if (
    !isLogEntryComplete({
      entryId: logEntry.id,
      logEntry,
      errors,
      formValues,
      logFields,
    })
  ) {
    return EntryStatus.INCOMPLETE;
  }
  return logEntry.reviewedBy
    ? EntryStatus.COMPLETE_REVIEWED
    : EntryStatus.COMPLETE_NOT_REVIEWED;
}
